import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AntiMemLeak } from 'src/app/core/form-utils/anti-mem-leak/anti-mem-leak';
import { ProviderService } from 'src/app/core/provider.service';
import { Observable } from 'rxjs';
import { DisaggregationModel } from './models/disaggregation-model';
import { TreeNode } from 'primeng/api';
import { TreeSelectModule } from 'primeng/treeselect';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-disaggregation-filter',
  templateUrl: './disaggregation-filter.component.html',
  styleUrls: ['./disaggregation-filter.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, TreeSelectModule, TranslateModule],
})
export class DisaggregationFilterComponent extends AntiMemLeak implements OnInit, AfterViewInit {
  @Input()
  resetFilterObservable?: Observable<any>;
  @Input({ required: true })
  formGroupInput!: FormGroup;
  @Input()
  formControlNameInput = 'disaggregations';
  @Input()
  label?: string;
  @Input()
  level: 1 | 2 = 1;
  @Input()
  initializeOnStart: boolean = true;

  disaggregations: DisaggregationModel[] = [];
  treeNodes: TreeNode[] = [];
  selectedNodes: TreeNode[] | null = null;
  loading = true;

  get isFormControlReady(): boolean {
    return !!this.formGroupInput?.get(this.formControlNameInput);
  }

  constructor(
    private providerService: ProviderService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.initializeOnStart) {
      this.getDisaggregationList();
    }
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.resetFilterObservable?.subscribe(() => {
        this.selectedNodes = null;
        this.formGroupInput.get(this.formControlNameInput)?.setValue([]);
      })
    );

    const currentValue = this.formGroupInput.get(this.formControlNameInput)?.value;
    if (currentValue && Array.isArray(currentValue) && currentValue.length > 0) {
      this.selectedNodes = this.convertDisaggregationsToTreeNodes(currentValue);
    }
  }

  clearFilter(): void {
    this.selectedNodes = null;
    this.formGroupInput.get(this.formControlNameInput)?.setValue([], { emitEvent: true });
  }

  async getDisaggregationList(): Promise<void> {
    this.loading = true;
    if (this.disaggregations && this.disaggregations.length > 0) {
      return;
    }
    try {
      this.disaggregations = await this.providerService.disaggregationFilterService.getDisaggregationsList(this.level);
      this.buildTreeNodes();
      const currentValue = this.formGroupInput.get(this.formControlNameInput)?.value;
      if (currentValue && Array.isArray(currentValue) && currentValue.length > 0) {
        this.selectedNodes = this.convertDisaggregationsToTreeNodes(currentValue);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  buildTreeNodes(): void {
    const categories = Array.from(new Set(this.disaggregations.map((d) => d.disaggregationCat)));
    const dynamicNodes: TreeNode[] = categories.map((category) => {
      const categoryDisaggregations = this.disaggregations.filter((d) => d.disaggregationCat === category);

      return {
        label: category ?? '',
        data: category,
        key: `category-${category}`,
        selectable: true,
        children: categoryDisaggregations.map((disaggregation) => ({
          label: disaggregation.disaggregationDescription || '',
          data: disaggregation,
          key: `disaggregation-${disaggregation.disaggregationId}`,
          selectable: true,
        })),
      };
    });

    const totalNode: TreeNode = {
      label: 'Total',
      data: {
        disaggregationId: '',
        disaggregationDescription: 'total',
        disaggregationCat: '',
        disaggregationAbb: '',
      },
      key: 'disaggregation-total',
      selectable: true,
      checked: true,
    };

    this.treeNodes = [totalNode, ...dynamicNodes];
  }

  findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }
      if (node.children) {
        const found = this.findNodeByKey(node.children, key);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  onSelectionChange(selectedTreeNodes: TreeNode[] | null): void {
    const selectedDisaggregations = this.convertTreeNodesToDisaggregations(selectedTreeNodes);

    this.formGroupInput.get(this.formControlNameInput)?.setValue(selectedDisaggregations, { emitEvent: true });
  }

  convertTreeNodesToDisaggregations(nodes: TreeNode[] | null): DisaggregationModel[] {
    if (!nodes || nodes.length === 0) {
      return [];
    }

    const disaggregations: DisaggregationModel[] = [];
    nodes.forEach((node) => {
      if (node.data && node.data.disaggregationId != null) {
        disaggregations.push(node.data as DisaggregationModel);
      }
    });

    return disaggregations;
  }

  convertDisaggregationsToTreeNodes(disaggregations: DisaggregationModel[]): TreeNode[] {
    if (!disaggregations || disaggregations.length === 0) {
      return [];
    }

    const selectedNodes: TreeNode[] = [];

    disaggregations.forEach((disaggregation) => {
      const searchKey =
        disaggregation.disaggregationId === ''
          ? 'disaggregation-total'
          : `disaggregation-${disaggregation.disaggregationId}`;

      const node = this.findNodeByKey(this.treeNodes, searchKey);

      if (node) {
        selectedNodes.push(node);
      }
    });

    return selectedNodes;
  }
  public syncDisaggregations(selectedItems: DisaggregationModel[]): void {
    if (!selectedItems || selectedItems.length === 0) {
      this.selectedNodes = null;
      this.formGroupInput.get(this.formControlNameInput)?.setValue([], { emitEvent: false });

      if (!this.disaggregations || this.disaggregations.length === 0) {
        this.getDisaggregationList();
      }

      this.cdr.detectChanges();
      return;
    }

    if (this.disaggregations && this.disaggregations.length > 0) {
      this.assignFilterValue(selectedItems);
    } else {
      this.getDisaggregationList().then(() => this.assignFilterValue(selectedItems));
    }
  }

  private assignFilterValue(selectedItems: DisaggregationModel[]): void {
    const totalData: DisaggregationModel = {
      disaggregationId: '',
      disaggregationDescription: 'total',
      disaggregationCat: '',
      disaggregationAbb: '',
    };

    const allValidOptions = [totalData, ...this.disaggregations];

    const matchedItems = allValidOptions.filter((option: any) =>
      selectedItems.some((selected: any) => selected.disaggregationId === option.disaggregationId)
    );

    this.selectedNodes = this.convertDisaggregationsToTreeNodes(matchedItems);
    this.formGroupInput.get(this.formControlNameInput)?.setValue(matchedItems, { emitEvent: false });

    this.cdr.detectChanges();
  }
}
